import { activityGuardTimerAlert, activityGuardTimerTimeout } from 'Config';
import { useAppSelector } from 'Hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LogoutWarningAndNotificationModal } from './LogoutWarningAndNotificationModal';

const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';

const userDetectionEvents: string[] = [
  'mousemove',
  'keypress',
  'click',
  'keydown',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'focus',
  'DOMMouseScroll',
];

const getLastActivity = (): number => {
  const stored = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  return stored ? Number(stored) : Date.now();
};

const setLastActivity = (timestamp: number) => {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
};

const isDevOrQa =
  import.meta.env.DEV || import.meta.env.VITE_APP_ENV_NAME === 'qa';

const ActivityGuardContent: React.FC<any> = (props) => {
  const lastActivityRef = useRef<number>(getLastActivity());
  const isTimedOutRef = useRef<boolean>(false);
  const [timeoutSeconds, setTimeoutSeconds] = useState<number>(() => {
    const elapsedSeconds = (Date.now() - lastActivityRef.current) / 1000;
    return Math.max(0, Math.floor(activityGuardTimerTimeout - elapsedSeconds));
  });
  const [showLogoutWarningModal, setShowLogoutWarningModal] =
    useState<boolean>(false);

  const { authenticatedUser } = useAppSelector(({ imposter }) => imposter);
  const isUser = authenticatedUser?.israeliId;

  const calculateRemainingTime = useCallback(() => {
    const elapsedSeconds = (Date.now() - lastActivityRef.current) / 1000;
    return Math.max(0, Math.floor(activityGuardTimerTimeout - elapsedSeconds));
  }, []);

  useEffect(() => {
    if (!isUser) return;

    const handleUserActivity = () => {
      if (!isTimedOutRef.current) {
        const now = Date.now();
        lastActivityRef.current = now;
        setLastActivity(now);
      }
    };

    const intervalId = setInterval(() => {
      setTimeoutSeconds(calculateRemainingTime());
    }, 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeoutSeconds(calculateRemainingTime());
      }
    };

    userDetectionEvents.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      userDetectionEvents.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [isUser, calculateRemainingTime]);

  useEffect(() => {
    if (timeoutSeconds <= activityGuardTimerAlert && timeoutSeconds > 0)
      setShowLogoutWarningModal(true);

    if (timeoutSeconds <= 0) {
      isTimedOutRef.current = true;
      sessionStorage.clear();
    }
  }, [timeoutSeconds]);

  return (
    <>
      {showLogoutWarningModal && (
        <LogoutWarningAndNotificationModal
          isWarning={timeoutSeconds > 0}
          closeModal={() => setShowLogoutWarningModal(false)}
        />
      )}
      {props.children}
    </>
  );
};

export const ActivityGuard: React.FC<any> = (props) => {
  if (isDevOrQa) {
    return <>{props.children}</>;
  }
  return <ActivityGuardContent>{props.children}</ActivityGuardContent>;
};
